import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./AddNewProject.scss";
import Button from "../../../Components/Common/Button/Button";
import useCreateProject from "../../../hooks/useCreateProject";
import {
  INITIAL_VALUES,
  STEP_FIELDS,
  STEP_LABELS,
  FIELD_ORDER,
  stepSchemas,
  fullSchema,
  buildReviewData,
} from "./form/formConfig";
import { normalizeApiErrors } from "./form/apiErrorMap";
import Step1ProjectType from "./form/steps/Step1ProjectType";
import Step2ProjectInfo from "./form/steps/Step2ProjectInfo";
import Step3Contacts from "./form/steps/Step3Contacts";
import Step4Lender from "./form/steps/Step4Lender";
import Step5Requirements from "./form/steps/Step5Requirements";
import Step6Review from "./form/steps/Step6Review";
import TickIcon from "Assets/Img/TickIcon";
import StepTick from "Assets/Img/StepTick";
import { NewProjectFormValues } from "./form/types";
import { ValidationError } from "yup";

const NUMERIC_FIELDS = new Set(["reqPhone", "lenderPhone", "projValue"]);

const toFormikErrors = (yupError: unknown): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!(yupError instanceof ValidationError) || !yupError.inner) return errors;

  yupError.inner.forEach((err) => {
    if (err.path && !errors[err.path]) {
      errors[err.path] = err.message;
    }
  });

  return errors;
};

const toTouchedObject = (fields: string[]): Record<string, boolean> =>
  fields.reduce<Record<string, boolean>>((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

const scrollToFirstError = (errors: Record<string, string>) => {
  for (const key of FIELD_ORDER) {
    if (!errors[key]) continue;
    const byId = document.getElementById(key);
    const byName = document.querySelector<HTMLElement>(`[name="${key}"]`);
    const el = byId || byName;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus?.();
      return;
    }
  }
};

const getDashboardPath = (): string => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") as {
      role?: string;
    };
    return user?.role === "lender" ? "/bank-dashboard" : "/admin";
  } catch {
    return "/admin";
  }
};

const STORAGE_KEY = "addNewProject_draft";

const CONTACT_ROLE_LABEL = {
  owner: "Owner",
  gc: "GC",
  architect: "Architect",
  lender: "Lender",
} as const;

interface CreatedContactState {
  createdBank?: {
    bankId: string;
    bankName: string;
    branch?: { id: string; name: string };
  };
  createdUser?: {
    role: "owner" | "gc" | "architect" | "lender";
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      address?: string;
    };
  };
}

const getSavedValues = (): Partial<NewProjectFormValues> | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<NewProjectFormValues>) : null;
  } catch {
    return null;
  }
};

type StepStatus = "inactive" | "active" | "done";

interface StepIndicator {
  step: number;
  status: StepStatus;
}

export default function AddNewProject() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const s = Number(searchParams.get("step"));
    return s >= 1 && s <= 6 ? s : 1;
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [showSuccess]);

  const { handleCreateProject, loading: submitLoading } = useCreateProject();

  const formik = useFormik<NewProjectFormValues>({
    initialValues: { ...INITIAL_VALUES, ...(getSavedValues() ?? {}) },
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: () => {},
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formik.values));
    } catch {}
  }, [formik.values]);

  // Returning from Manage Users after creating a contact: select that user in the matching dropdown.
  useEffect(() => {
    const returned = location.state as CreatedContactState | null;
    const bankReturned = returned?.createdBank;
    if (bankReturned) {
      formik.setFieldValue("lenderBankId", bankReturned.bankId);
      formik.setFieldValue("bankName", bankReturned.bankName);
      formik.setFieldValue("lenderBranchId", bankReturned.branch?.id ?? "");
      formik.setFieldValue("bankLocation", bankReturned.branch?.name ?? "");
      formik.setFieldValue("lendingOfficerId", "");
      formik.setFieldValue("lenderName", "");
      formik.setFieldValue("lenderPhone", "");
      formik.setFieldValue("lenderEmail", "");
      formik.setFieldValue("lenderAddr", "");
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
      return;
    }
    const created = returned?.createdUser;
    if (!created) return;
    const { role, user } = created;
    const name = `${user.firstName} ${user.lastName}`;
    if (role === "owner") {
      formik.setFieldValue("ownerId", user.id);
      formik.setFieldValue("ownerName", name);
      formik.setFieldValue("ownerPrimaryContact", "");
      formik.setFieldValue("ownerPhone", user.phone ?? "");
      formik.setFieldValue("ownerEmail", user.email);
      formik.setFieldValue("ownerAddr", user.address ?? "");
    } else if (role === "lender") {
      formik.setFieldValue("lendingOfficerId", user.id);
      formik.setFieldValue("lenderName", name);
      formik.setFieldValue("lenderPhone", user.phone ?? "");
      formik.setFieldValue("lenderEmail", user.email);
      formik.setFieldValue("lenderAddr", user.address ?? "");
    } else if (role === "gc") {
      formik.setFieldValue("gcId", user.id);
      formik.setFieldValue("gcName", name);
      formik.setFieldValue("gcPrimaryContact", "");
      formik.setFieldValue("gcPhone", user.phone ?? "");
      formik.setFieldValue("gcEmail", user.email);
      formik.setFieldValue("gcAddr", user.address ?? "");
    } else {
      formik.setFieldValue("architect", user.id);
      formik.setFieldValue("architectName", name);
    }
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formik.values));
    } catch {}
  };

  const handleManageBankRedirect = (mode: "bank" | "branch") => {
    saveDraft();
    navigate("/manage-bank", {
      state: {
        manageBankFor: {
          mode,
          bankId: formik.values.lenderBankId,
          returnTo: `${location.pathname}?step=${currentStep}`,
        },
      },
    });
  };

  const handleCreateUserRedirect = (
    role: "owner" | "gc" | "architect" | "lender",
  ) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formik.values));
    } catch {}
    navigate("/manage-users", {
      state: {
        createUserFor: {
          role: CONTACT_ROLE_LABEL[role],
          returnTo: `${location.pathname}?step=${currentStep}`,
          // A lender user needs the bank/branch already chosen on this step.
          ...(role === "lender" && {
            prefill: {
              bankId: formik.values.lenderBankId,
              bankName: formik.values.bankName,
              branchId: formik.values.lenderBranchId,
            },
          }),
        },
      },
    });
  };

  const clearError = (name: string) => {
    if (!formik.errors[name as keyof NewProjectFormValues]) return;
    const newErrors = { ...formik.errors };
    delete newErrors[name as keyof NewProjectFormValues];
    formik.setErrors(newErrors);
  };

  const handleSetFieldValue = (name: string, value: unknown) => {
    formik.setFieldValue(name, value);
    if (Array.isArray(value) ? value.length : value) clearError(name);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    const baseField = name.startsWith("requirements.")
      ? name.split(".")[1]
      : name;
    const sanitized = NUMERIC_FIELDS.has(baseField)
      ? value.replace(/\D/g, "")
      : value;
    formik.setFieldValue(name, sanitized);
    if (sanitized) clearError(name);
  };

  const goTo = (step: number) => {
    setCurrentStep(step);
    setSearchParams({ step: String(step) }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateCurrentStep = async (): Promise<boolean> => {
    const schema = stepSchemas[currentStep - 1];
    const fields = STEP_FIELDS[currentStep] ?? [];

    try {
      await schema.validate(formik.values, { abortEarly: false });
      return true;
    } catch (err) {
      const stepErrors = toFormikErrors(err);
      formik.setTouched(
        { ...formik.touched, ...toTouchedObject(fields) },
        false,
      );
      formik.setErrors({ ...formik.errors, ...stepErrors });
      scrollToFirstError(stepErrors);
      return false;
    }
  };

  const handleNext = async () => {
    const hasVisibleCharError = Array.from(
      document.querySelectorAll<HTMLElement>(".field-error"),
    ).some((el) => el.offsetParent !== null);
    if (hasVisibleCharError) {
      const el = Array.from(
        document.querySelectorAll<HTMLElement>(".field-error"),
      ).find((el) => el.offsetParent !== null);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const valid = await validateCurrentStep();
    if (!valid) return;
    if (currentStep < 6) goTo(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) goTo(currentStep - 1);
  };

  const handleFinalSubmit = async () => {
    setSubmitError("");

    try {
      await fullSchema.validate(formik.values, { abortEarly: false });
    } catch (err) {
      const allErrors = toFormikErrors(err);
      formik.setErrors(allErrors);
      setSubmitError("Please fix validation errors before submitting.");
      const firstErrorStep = allErrors.projectType
        ? 1
        : allErrors.projName ||
            allErrors.projValue ||
            allErrors.projDesc ||
            allErrors.projAddr
          ? 2
          : allErrors.ownerId || allErrors.reqSigner || allErrors.gcId
            ? 3
            : allErrors.lenderBankId ||
                allErrors.lenderBranchId ||
                allErrors.lendingOfficerId
              ? 4
              : 5;
      goTo(firstErrorStep);
      scrollToFirstError(allErrors);
      return;
    }

    const result = await handleCreateProject(
      formik.values,
      formik.values.requirements,
      formik.values.projectType,
    );

    if (!result.success) {
      const { formikErrors, touchedFields, firstStep } = normalizeApiErrors(
        result.fields,
      );

      if (Object.keys(formikErrors).length > 0) {
        formik.setErrors(formikErrors);
        formik.setTouched({ ...formik.touched, ...touchedFields }, false);
        setSubmitError("Please correct the highlighted fields.");
        if (firstStep) goTo(firstStep);
        scrollToFirstError(formikErrors);
      } else {
        setSubmitError(result.message || "Failed to create project.");
      }
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
    setShowSuccess(true);
  };

  const resetForm = () => {
    localStorage.removeItem(STORAGE_KEY);
    formik.resetForm();
    goTo(1);
    setShowSuccess(false);
    setSubmitError("");
  };

  const stepIndicators: StepIndicator[] = Array.from({ length: 6 }, (_, i) => {
    const step = i + 1;
    let status: StepStatus = "inactive";
    if (step < currentStep) status = "done";
    if (step === currentStep) status = "active";
    return { step, status };
  });

  const reviewData = buildReviewData(formik.values);

  if (showSuccess) {
    return (
      <div className="addNewProject">
        <div className="page">
          <div className="card">
            <div className="successScreen">
              <div className="successIcon">
                <TickIcon />
              </div>
              <div className="successTitle">Project Created</div>
              <div className="successSub">
                "{formik.values.projName || "New Project"}" has been created and
                is now active in the dashboard.
              </div>
              <div className="successActionsWrap">
                <div className="successActions">
                  <Button
                    className="btn btnPrimary"
                    onClick={() => navigate(getDashboardPath())}
                  >
                    View Dashboard
                  </Button>
                  <Button className="btn btnSecondary" onClick={resetForm}>
                    + Add Another Project
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="addNewProject">
      <div className="page">
        <div className="steps" id="stepIndicator">
          {stepIndicators.map((item) => (
            <div
              key={item.step}
              className={`stepItem ${item.status}`}
              data-step={item.step}
            >
              <div className="stepDot">
                {item.status === "done" ? <StepTick /> : item.step}
              </div>
              <div className="stepLabel">{STEP_LABELS[item.step - 1]}</div>
            </div>
          ))}
        </div>

        {currentStep === 1 && (
          <Step1ProjectType
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={handleSetFieldValue}
            onNext={handleNext}
          />
        )}
        {currentStep === 2 && (
          <Step2ProjectInfo
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            onChange={handleChange}
            setFieldValue={handleSetFieldValue}
            onBack={handleBack}
            onNext={handleNext}
          />
        )}
        {currentStep === 3 && (
          <Step3Contacts
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={handleSetFieldValue}
            onChange={handleChange}
            onBack={handleBack}
            onNext={handleNext}
            onCreateUser={
              getDashboardPath() === "/admin"
                ? handleCreateUserRedirect
                : undefined
            }
          />
        )}
        {currentStep === 4 && (
          <Step4Lender
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={handleSetFieldValue}
            onChange={handleChange}
            onBack={handleBack}
            onNext={handleNext}
            onCreateUser={
              getDashboardPath() === "/admin"
                ? () => handleCreateUserRedirect("lender")
                : undefined
            }
            onCreateBank={
              getDashboardPath() === "/admin"
                ? () => handleManageBankRedirect("bank")
                : undefined
            }
            onCreateBranch={
              getDashboardPath() === "/admin"
                ? () => handleManageBankRedirect("branch")
                : undefined
            }
          />
        )}
        {currentStep === 5 && (
          <Step5Requirements
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={handleSetFieldValue}
            onChange={handleChange}
            onBack={handleBack}
            onNext={handleNext}
          />
        )}
        {currentStep === 6 && (
          <Step6Review
            values={formik.values}
            reviewData={reviewData}
            submitError={submitError}
            submitLoading={submitLoading}
            onBack={handleBack}
            onSubmit={handleFinalSubmit}
          />
        )}
      </div>
    </div>
  );
}
